import { Alert, Grid, GridItem, Heading, Link, Search, SearchResultRow, SearchResults, Text, } from '@digitools/honest-ui';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Notifications, { FunctionalArea } from '../../../../components/Notifications/Notifications';
import PageLoader from '../../../../components/PageLoader/PageLoader';
import { TEALIUM_EVENT, TEALIUM_PAGE_VIEW } from '../../../../constants/tealiumConstants';
import useAuthentication from '../../../../hooks/useAuthentication';
import useTealium from '../../../../hooks/useTealium';
import { ExperienceContext } from '../../../../providers/ExperienceProvider';
import getDisplayText from '../../../../utils/languageDisplay';
import { searchCustomers as searchCustomersApi } from './../../../../api/ConfigApi';
import './CustomerSearchLandingPage.css';
import { MultiCustomerDisplayTextEn } from './i18n/MultiCustomerDisplayTextEn';
import { MultiCustomerDisplayTextEs } from './i18n/MultiCustomerDisplayTextEs';
import UnassociatedMessage from './UnassociatedMessage';
/** Queries shorter than this don't hit the search API - the customer list is shown as-is instead. */
const MINIMUM_QUERY_LENGTH = 3;
/** DOM id linking <Search aria-controls> to its <SearchResults> region for arrow-key navigation. */
const RESULTS_REGION_ID = 'customer-list';
const getCustomerName = (customer, displayCustDivSerial) => {
    let divSerial = '';
    if (displayCustDivSerial && customer.custDiv && customer.custSeral) {
        divSerial = customer.custDiv + '-' + customer.custSeral + ' ';
    }
    if (!!customer.customerDisplayName) {
        return divSerial + customer.customerDisplayName;
    }
    else if (!!customer.customerInternalName) {
        return divSerial + customer.customerInternalName;
    }
    else {
        console.info('No Customer Name available for customer: ', customer.customerId);
        return '';
    }
};
const CustomerSearchLandingPage = () => {
    const { user, authHeader, setCustomerInContext } = useAuthentication();
    const { language } = useContext(ExperienceContext);
    const [customerList, setCustomerList] = useState([]);
    const [filteredCustomerList, setFilteredCustomerList] = useState([]);
    const [displayDivSerial, setDisplayDivSerial] = useState(false);
    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showPerformanceAlert, setShowPerformanceAlert] = useState(true);
    // Drives the in-field hint: the short placeholder expands to the full instruction
    // once the field has focus. Tracks focus rather than click so tabbing in counts too.
    const [searchFocused, setSearchFocused] = useState(false);
    const { trackView, trackEvent } = useTealium();
    // Guards against a slower earlier search resolving after a faster later one and
    // overwriting the newer results. <Search> debounces, so this is belt-and-braces.
    const latestQueryRef = useRef(0);
    useEffect(() => {
        if (!loading) {
            // add passed source_system to deal with customer search landing page having no customerInContext initially
            trackView({
                page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.CUSTOMER_SEARCH,
                page_l4: TEALIUM_PAGE_VIEW.PAGE_L4.CUSTOMER_SEARCH.CUSTOMER_SEARCH_LANDING_PAGE,
                source_system: '',
            });
        }
    }, [loading]);
    useEffect(() => {
        clearCustomerInContext().then(() => {
            if (user && user.accessAllCustomers) {
                if (user.userInternal) {
                    setDisplayDivSerial(true);
                }
                setLoading(false);
            }
            else {
                getAuthorizedCustomers();
            }
        });
    }, []);
    const getLocalDisplayText = (key) => getDisplayText.apply({}, [key, MultiCustomerDisplayTextEn, MultiCustomerDisplayTextEs, language]);
    const getAuthorizedCustomers = () => {
        if (user && user.customers) {
            setCustomerList(user.customers);
            setFilteredCustomerList(user.customers);
            setLoading(false);
        }
        else if (user && !user.customers) {
            setCustomerList([]);
            setFilteredCustomerList([]);
            setLoading(false);
        }
    };
    /**
     * Called with the debounced query by <Search>, and directly (with '') by its
     * Reset button - a cleared field is just a below-minimum query, so it takes
     * the same early-return path and needs no separate reset handler.
     */
    const handleQueryChange = async (nextQuery) => {
        const requestId = ++latestQueryRef.current;
        /** This call is no longer the newest one, so its result must not be applied. */
        const stale = () => requestId !== latestQueryRef.current;
        setQuery(nextQuery);
        setError(false);
        if (nextQuery.length < MINIMUM_QUERY_LENGTH) {
            setSearching(false);
            setFilteredCustomerList(customerList);
            return;
        }
        try {
            setSearching(true);
            const result = await searchCustomersApi(nextQuery, authHeader());
            if (stale())
                return;
            setFilteredCustomerList(result);
        }
        catch (e) {
            if (stale())
                return;
            setError(true);
            setFilteredCustomerList([]);
        }
        finally {
            if (!stale()) {
                setSearching(false);
            }
        }
    };
    const updateCustomerInContext = async (customerId) => {
        if (setCustomerInContext) {
            try {
                setLoading(true);
                if (user && user.roles && user.roles.length === 1 && user.roles.includes('employee')) {
                    await setCustomerInContext(customerId, '/employee/home');
                }
                else {
                    await setCustomerInContext(customerId, '/employer/home');
                }
            }
            catch (e) {
                console.error('issue setting customer');
            }
        }
        trackEvent({
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.CUSTOMER_SEARCH,
            page_l4: TEALIUM_PAGE_VIEW.PAGE_L4.CUSTOMER_SEARCH.CUSTOMER_SEARCH_LANDING_PAGE,
            event_action: TEALIUM_EVENT.EVENT_ACTION.SELECT,
            event_name: 'select a customer',
            event_type: TEALIUM_EVENT.EVENT_TYPE.SELECT_CUSTOMER,
        });
    };
    const clearCustomerInContext = async () => {
        if (setCustomerInContext) {
            try {
                await setCustomerInContext('');
            }
            catch (e) {
                console.error('Issue clearing customer in context');
            }
        }
    };
    if (user && user.roles && user.roles.indexOf('client_service_reps') > -1) {
        const url = window.location.hostname.indexOf('np-') > -1
            ? 'https://admin.np-mylincolnportal.com/'
            : 'https://admin.mylincolnportal.com/';
        // honest-ui's <Alert> doesn't spread unknown props, so data-public sits on a wrapper.
        return (<div data-public={true}>
        <Alert type={'error'}>
          <strong>Account no longer valid.</strong> Please note that as of 9/1/19, the login process has changed for
          LFG employees. Please go to{' '}
          <Link href={url} linkType={'external'} language={language}>
            {url}
          </Link>{' '}
          and login with your LFG email address and password.
        </Alert>
      </div>);
    }
    // A query below the minimum length leaves the list untouched, so the results region
    // stays in its `idle` state rather than announcing a search nobody ran.
    const searchedQuery = query.length >= MINIMUM_QUERY_LENGTH ? query : '';
    const resultRows = filteredCustomerList.map((customer, idx) => (<SearchResultRow key={customer.customerId ?? idx} data-public={true} data-testid={`customer-${idx}-link`} onClick={() => updateCustomerInContext(customer.customerId)}>
      {getCustomerName(customer, displayDivSerial)}
    </SearchResultRow>));
    // Users with their own book of business get a browsable list up front, so "has rows"
    // isn't the same as "has searched" - keep both. The heading only earns its place once
    // there are rows under it; loading and no-match states render their message alone.
    const hasRows = resultRows.length > 0;
    // An accessAllCustomers user starts with an empty list, so without this gate the
    // region renders as a bare heading over an empty frame before the first search.
    const showResults = hasRows || searching || searchedQuery !== '';
    return (
    // App lays every route out on honest-ui's <Grid> (see MainContainer in App.tsx),
    // so this page no longer wraps itself - a nested Grid would double the gutters.
    <>
      <Heading data-public={true} as={'h1'} className={'customer-search-heading'}>
        {getLocalDisplayText('pageHeading')}
      </Heading>
      {/* honest-ui zeroes margins on <Text> and the <Search> label alike, so this intro
            copy would otherwise sit flush against the 'Search' label below it. */}
      <Text as={'p'} className={'hui-mb-lg'}>
        {getLocalDisplayText('subHeading')}
      </Text>
      {error ? (<div data-public={true} data-testid={'error'}>
          <Alert type={'error'}>{getLocalDisplayText('errorMessage')}</Alert>
        </div>) : (<>
          <Notifications functionalArea={FunctionalArea.MULTI_CUSTOMER_LANDING}/>
          {/* The wrapper carries the bottom spacing: <Alert> has no margin of its own,
                so otherwise the box sits flush against the 'Search' label below it. */}
          {user && user.accessAllCustomers && (<div data-public={true} className={'hui-mb-lg'}>
              <Alert type={'info'} dismissible={true} isOpen={showPerformanceAlert} onOpenChange={setShowPerformanceAlert}>
                {getLocalDisplayText('performanceMessage')}
              </Alert>
            </div>)}
          {loading ? (<PageLoader id='customer-search-landing-page-spinner'/>) : (<>
              {user && !user.accessAllCustomers && customerList.length === 0 ? (<UnassociatedMessage />) : (<>
                  <Grid noGutter={true}>
                    <GridItem colSpan={4} colSpanMd={6} colSpanLg={6}>
                      {/* <Search> merges no caller className onto its root - its props spread
                      onto the inner <input> - so the Reset overrides hang off a wrapper. */}
                      <div className={'customer-search-field'}>
                        {/* The label is hidden rather than dropped: it stays in the DOM wired
                        up via htmlFor, so the field keeps its accessible name now that
                        the visible cue is the in-field placeholder. */}
                        <Search label={getLocalDisplayText('searchLabel')} hideLabel={true} placeholder={getLocalDisplayText(searchFocused ? 'searchPlaceholderFocused' : 'searchPlaceholder')} clearMode={'reset'} onChange={handleQueryChange} onReset={() => handleQueryChange('')} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} loading={searching} aria-controls={RESULTS_REGION_ID} language={language} data-testid={'search-input'}/>
                      </div>
                    </GridItem>
                  </Grid>
                  {/* <SearchResults> doesn't spread unknown props either - data-* lives on the wrapper.
  
                        No `idleMessage`: the prop can't suppress the idle copy (SearchResults
                        falls back to its own translated default for both undefined and null),
                        so the region is gated on `showResults` instead and never reaches the
                        idle state with empty children.
  
                        `rowsPerPage` hands the *whole* matching set over: SearchResults slices it
                        itself and renders its own <Paginator> bar below the results frame, so this
                        page neither pre-slices `children` nor owns any page state. 10 matches the
                        page size this list used before the rebrand. */}
                  {showResults && (<div data-public={true} data-testid={'customer-list'}>
                      <SearchResults id={RESULTS_REGION_ID} heading={hasRows ? getLocalDisplayText('customerHeading') : undefined} query={searchedQuery} count={filteredCustomerList.length} loading={searching} emptyMessage={getLocalDisplayText('noResults')} rowsPerPage={10} rowLabel={getLocalDisplayText('rowLabel')} language={language}>
                        {hasRows ? resultRows : undefined}
                      </SearchResults>
                    </div>)}
                  <Text as={'p'}>
                    {getLocalDisplayText('helpMessage')}
                  </Text>
                </>)}
            </>)}
        </>)}
    </>);
};
export default CustomerSearchLandingPage;
